// src/components/FormContext.js
import React, { createContext, useState, useCallback, useEffect } from "react";
import { validateField, validateForm } from "./validation";
import isEqual from "lodash/isEqual";

// Firestore imports (Firebase v9 Modular)
import { db } from "../util/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

// Auth import
import { useAuth } from "../util/auth";

// Analytics import
import Analytics from "../util/analytics";

export const FormContext = createContext();

// Initial Education Entry Structure
const initialEducationEntry = {
  educationType: "College",
  institutionName: "",
  city: "",
  stateProvince: "",
  country: "",
  degree: "",
  fieldOfStudy: "",
  minor: "",
  startDate: "",
  endDate: "",
  currentlyEnrolled: false,
  gpa: "",
  honors: "",
  awards: "",
  activities: "",
  societies: "",
  leadershipRoles: "",
  certifications: "",
  relevantCoursework: "",
};

// Initial Work Experience Entry Structure
const initialWorkExperienceEntry = {
  jobTitle: "",
  companyName: "",
  location: "",
  startDate: "",
  endDate: "",
  currentlyEmployed: false,
  jobDescriptionAndAchievements: "",
  supervisorName: "",
  supervisorContact: "",
  reasonForLeaving: "",
  skillsUtilized: "",
  technologiesUsed: "",
};

// Initial Target Job Entry Structure
const initialTargetJobEntry = {
  jobTitle: "",
  companyName: "",
  jobDescription: "",
  jobLocation: "",
  jobType: "",
  salary: "",
  applicationDeadline: "",
  generationOptions: {
    resume: true,
    coverLetter: true,
    references: false,
    recruiterMessage: false,
    linkedinOptimization: false,
    interviewPrep: false,
    networkingEmails: false,
    careerDevelopmentPlan: false,
    followUpEmails: false,
    salaryNegotiation: false,
    professionalBio: false,
    personalBranding: false,
    skillGapAnalysis: false,
    companyResearch: false,
  },
};

// Initial Qualification Entry Structure
const initialQualificationEntry = {
  qualificationType: "",
  details: {
    projectTitle: "",
    role: "",
    institution: "",
    startDate: "",
    endDate: "",
    description: "",
    outcomes: "",
  },
};

// Complete Initial Form Data Structure
const initialFormData = {
  personalInformation: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    linkedinUrl: "",
    websiteUrl: "",
    twitterHandle: "",
    githubUsername: "",
    otherSocialProfile: "",
    professionalTitle: "",
    summary: "",
    profilePhoto: null,
  },
  education: [initialEducationEntry],
  workExperience: [initialWorkExperienceEntry],
  targetJobInformation: [initialTargetJobEntry],
  additionalQualifications: [],
  noWorkExperience: false,
};

export const FormProvider = ({ children }) => {
  const auth = useAuth(); // Retrieve authenticated user

  // Initialize formData from localStorage or use initialFormData
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem("formData");
    const initialData = savedData ? JSON.parse(savedData) : initialFormData;
    return initialData;
  });

  const [errors, setErrors] = useState({});

  // Save formData to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData]);

  // Load data from Firestore on mount if user is authenticated
  useEffect(() => {
    const loadData = async () => {
      if (!auth.user) return;

      const startTime = performance.now();
      try {
        const userDocRef = doc(db, "users", auth.user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();

          if (data) {
            setFormData((prev) => ({
              ...prev,
              ...data,
            }));
            Analytics.trackFeatureUsage("form_data_loaded_firestore");
          }
        }
        const endTime = performance.now();
        Analytics.trackGenerationTime("form_data_load", endTime - startTime);
      } catch (error) {
        console.error("Error loading data from Firestore:", error);
      }
    };

    loadData();
  }, [auth.user]);

  // Function to validate a field and update errors state
  const validateFieldAndUpdateErrors = useCallback(
    (section, field, value) => {
      const error = validateField(field, value, formData);
      setErrors((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: error,
        },
      }));
      return error;
    },
    [formData]
  );

  // Function to update form data for a specific section
  const updateFormData = useCallback((section, data) => {
    setFormData((prev) => {
      const newData = typeof data === "function" ? data(prev[section]) : data;
      const prevSection = prev[section];
      // Log details of the update attempt
      console.log(
        `[FormContext] updateFormData called for section: "${section}"`
      );
      console.log("[FormContext] Received data for update:", data);
      console.log("[FormContext] Previous state for section:", prevSection);
      console.log("[FormContext] New data to be set for section:", newData);

      if (isEqual(prevSection, newData)) {
        console.log(
          "[FormContext] Data for section is the same, no update triggered."
        );
        return prev; // No update needed
      }
      Analytics.trackFormStep("form_section_update", section);
      console.log(`[FormContext] Updating section "${section}" in formData.`);
      return {
        ...prev,
        [section]: newData,
      };
    });
  }, []);

  // Function to reset the entire form data to initial state
  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    Analytics.trackFeatureUsage("form_data_reset");
  }, []);

  // Function to populate form data from payload, resetting existing data first
  const populateFormData = useCallback((payload) => {
    setFormData(() => {
      const newFormData = { ...initialFormData };

      for (const section in newFormData) {
        if (payload.hasOwnProperty(section)) {
          if (section === "workExperience") {
            newFormData[section] = payload[section].map((job) => ({
              ...job,
              jobDescriptionAndAchievements: Array.isArray(
                job.jobDescriptionAndAchievements
              )
                ? job.jobDescriptionAndAchievements.join("\n")
                : job.jobDescriptionAndAchievements,
            }));
          } else if (section === "additionalQualifications") {
            newFormData[section] = payload[section].map((qualification) => ({
              ...qualification,
              details: { ...qualification.details },
            }));
          } else {
            newFormData[section] = payload[section];
          }
        }
      }

      // Fallback: If the first education entry does not have educationType, set a default
      if (newFormData.education && newFormData.education.length > 0) {
        if (!newFormData.education[0].educationType) {
          newFormData.education[0].educationType = "College";
        }
      }
      return newFormData;
    });
  }, []);

  // Education Section Functions
  const addEducationEntry = useCallback(() => {
    setFormData((prev) => {
      Analytics.trackFeatureUsage("education_entry_added");
      return {
        ...prev,
        education: [...prev.education, initialEducationEntry],
      };
    });
  }, []);

  const updateEducationEntry = useCallback(
    (index, field, value) => {
      setFormData((prev) => {
        const updatedEducation = [...prev.education];
        updatedEducation[index] = {
          ...updatedEducation[index],
          [field]: value,
        };
        validateFieldAndUpdateErrors(`education[${index}]`, field, value);
        return { ...prev, education: updatedEducation };
      });
    },
    [validateFieldAndUpdateErrors]
  );

  const removeEducationEntry = useCallback((index) => {
    setFormData((prev) => {
      Analytics.trackFeatureUsage("education_entry_removed");
      return {
        ...prev,
        education: prev.education.filter((_, i) => i !== index),
      };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`education[${index}]`];
      return newErrors;
    });
  }, []);

  const setEducation = useCallback((newEducation) => {
    setFormData((prev) => ({
      ...prev,
      education: Array.isArray(newEducation)
        ? newEducation
        : [initialEducationEntry],
    }));
  }, []);

  // Work Experience Section Functions
  const addWorkExperienceEntry = useCallback(() => {
    setFormData((prev) => {
      Analytics.trackFeatureUsage("work_experience_entry_added");
      return {
        ...prev,
        workExperience: [...prev.workExperience, initialWorkExperienceEntry],
      };
    });
  }, []);

  const updateWorkExperienceEntry = useCallback(
    (index, field, value) => {
      setFormData((prev) => {
        const updatedWorkExperience = [...prev.workExperience];
        updatedWorkExperience[index] = {
          ...updatedWorkExperience[index],
          [field]: value,
        };
        validateFieldAndUpdateErrors(`workExperience[${index}]`, field, value);
        return { ...prev, workExperience: updatedWorkExperience };
      });
    },
    [validateFieldAndUpdateErrors]
  );

  const removeWorkExperienceEntry = useCallback((index) => {
    setFormData((prev) => {
      Analytics.trackFeatureUsage("work_experience_entry_removed");
      return {
        ...prev,
        workExperience: prev.workExperience.filter((_, i) => i !== index),
      };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`workExperience[${index}]`];
      return newErrors;
    });
  }, []);

  const setWorkExperience = useCallback((newWorkExperience) => {
    setFormData((prev) => ({
      ...prev,
      workExperience: Array.isArray(newWorkExperience)
        ? newWorkExperience
        : [initialWorkExperienceEntry],
    }));
  }, []);

  // Target Job Information Section Functions
  const addTargetJobEntry = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      targetJobInformation: [
        ...prev.targetJobInformation,
        initialTargetJobEntry,
      ],
    }));
  }, []);

  const updateTargetJobEntry = useCallback(
    (index, field, value) => {
      setFormData((prev) => {
        const updatedTargetJobs = [...prev.targetJobInformation];
        if (field.startsWith("generationOptions.")) {
          const [, optionName] = field.split(".");
          updatedTargetJobs[index] = {
            ...updatedTargetJobs[index],
            generationOptions: {
              ...updatedTargetJobs[index].generationOptions,
              [optionName]: value,
            },
          };
        } else {
          updatedTargetJobs[index] = {
            ...updatedTargetJobs[index],
            [field]: value,
          };
        }
        validateFieldAndUpdateErrors(
          `targetJobInformation[${index}]`,
          field,
          value
        );
        return { ...prev, targetJobInformation: updatedTargetJobs };
      });
    },
    [validateFieldAndUpdateErrors]
  );

  const removeTargetJobEntry = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      targetJobInformation: prev.targetJobInformation.filter(
        (_, i) => i !== index
      ),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`targetJobInformation[${index}]`];
      return newErrors;
    });
  }, []);

  const setTargetJobInformation = useCallback((newTargetJobs) => {
    setFormData((prev) => ({
      ...prev,
      targetJobInformation: Array.isArray(newTargetJobs)
        ? newTargetJobs
        : [initialTargetJobEntry],
    }));
  }, []);

  // Additional Qualifications Section Functions
  const addQualificationEntry = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      additionalQualifications: [
        ...(prev.additionalQualifications || []),
        initialQualificationEntry,
      ],
    }));
  }, []);

  const updateQualificationEntry = useCallback(
    (index, field, value) => {
      setFormData((prev) => {
        const updatedQualifications = [
          ...(prev.additionalQualifications || []),
        ];
        if (field.startsWith("details.")) {
          const [, detailField] = field.split(".");
          updatedQualifications[index] = {
            ...updatedQualifications[index],
            details: {
              ...updatedQualifications[index].details,
              [detailField]: value,
            },
          };
        } else {
          updatedQualifications[index] = {
            ...updatedQualifications[index],
            [field]: value,
          };
        }
        validateFieldAndUpdateErrors(
          `additionalQualifications[${index}]`,
          field,
          value
        );
        return { ...prev, additionalQualifications: updatedQualifications };
      });
    },
    [validateFieldAndUpdateErrors]
  );

  const removeQualificationEntry = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      additionalQualifications: (prev.additionalQualifications || []).filter(
        (_, i) => i !== index
      ),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`additionalQualifications[${index}]`];
      return newErrors;
    });
  }, []);

  // Toggle for No Work Experience
  const toggleNoWorkExperience = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      noWorkExperience: value,
      workExperience: value ? [] : [initialWorkExperienceEntry],
    }));
  }, []);

  // Handle Form Submission
  const handleSubmit = useCallback(async () => {
    const startTime = performance.now();
    Analytics.trackFeatureUsage("form_submission_attempt");

    const formErrors = validateForm(formData);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      Analytics.trackError(
        { name: "FormValidationError", message: "Form validation failed" },
        "FormSubmission"
      );
      return;
    }

    try {
      // Your submission logic here
      const endTime = performance.now();
      Analytics.trackGenerationTime("form_submission", endTime - startTime);
      Analytics.trackFeatureUsage("form_submission_success");
    } catch (error) {
      Analytics.trackError(error, "FormSubmission");
      setErrors((prev) => ({
        ...prev,
        submit: error.message,
      }));
    }
  }, [formData]);

  return (
    <FormContext.Provider
      value={{
        formData,
        errors,
        updateFormData,
        addEducationEntry,
        updateEducationEntry,
        removeEducationEntry,
        setEducation,
        addWorkExperienceEntry,
        updateWorkExperienceEntry,
        removeWorkExperienceEntry,
        setWorkExperience,
        addTargetJobEntry,
        updateTargetJobEntry,
        removeTargetJobEntry,
        setTargetJobInformation,
        addQualificationEntry,
        updateQualificationEntry,
        removeQualificationEntry,
        toggleNoWorkExperience,
        handleSubmit,
        validateFieldAndUpdateErrors,
        populateFormData,
        resetFormData,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export default FormProvider;
